import React, { useState } from 'react';
import { 
  FiTool, 
  FiRotateCw, 
  FiSettings, 
  FiRadio, 
  FiActivity, 
  FiZap, 
  FiCpu, 
  FiWifi, 
  FiSun, 
  FiTerminal, 
  FiDollarSign,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiThermometer,
  FiTrash2,
  FiRotateCcw 
} from 'react-icons/fi';
import { DeviceToolGuard } from '../../../../auth/PermissionGuard';
import CommandNotification from '../../../../common/CommandNotification';
import './DeviceManagementTools.css';

const DeviceManagementTools = ({ 
  block, 
  onPrime, 
  onProximity, 
  onReset, 
  onPaymentConfirmed, 
  onMotorControl, 
  onRunDiagnostics, 
  onSensorControl, 
  onUVControl,
  onSetMode,
  onClearErrors,
  onClearData,
  onFactoryReset,
  onConfirmDangerousAction,
  userRole,
  isAdmin,
  isFranchisee,
  isOperator,
  isLoading = false 
}) => {
  // Role-based mode options - moved up to use for initialization
  const getModeOptions = () => {
    if (isOperator && isOperator()) {
      // Operators see only Paid/Offline and Set Mode
      return [
        { value: 'PAID', label: 'Paid' },
        { value: 'OFFLINE', label: 'Offline' }
      ];
    } else {
      // Franchisees and Administrators see all modes
      return [
        { value: 'FREE', label: 'Free' },
        { value: 'PAID', label: 'Paid' },
        { value: 'OFFLINE', label: 'Offline' },
        { value: 'TEST_FREE', label: 'Test: Free' },
        { value: 'TEST_PAID', label: 'Test: Paid' }
      ];
    }
  };

  const [activeToolCategory, setActiveToolCategory] = useState('favorites');
  const [dispenseCount, setDispenseCount] = useState(1);
  const [paymentId, setPaymentId] = useState('');
  const [pricePaid, setPricePaid] = useState('');
  const [sunscreenType, setSunscreenType] = useState('Coral_Care_SPF30');
  const [motorCommand, setMotorCommand] = useState('DISPENSE');
  const [motorValue, setMotorValue] = useState(1.5);
  // Initialize deviceMode with the first available option from the dropdown
  const modeOptions = getModeOptions();
  const [deviceMode, setDeviceMode] = useState(modeOptions[0]?.value || 'FREE');
  
  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    success: false,
    message: '',
    errorCode: null
  });

  // getModeOptions is now defined above for initialization

  const { serial, errors = [], warnings = [] } = block || {};

  const toolCategories = [
    { id: 'favorites', label: 'Favorites', icon: FiTool },
    { id: 'hardware', label: 'Hardware', icon: FiRadio },
    { id: 'admin', label: 'Admin', icon: FiSettings }
  ];

  // Helper function to show notifications
  const showNotification = (success, message, errorCode = null) => {
    setNotification({
      show: true,
      success,
      message,
      errorCode
    });
  };

  // Motor control error code mapping
  const getMotorErrorMessage = (errorCode) => {
    const errorMessages = {
      '-1': 'Invalid command format. Please check the command syntax.',
      '-2': 'Motor is already running. Please stop it first.',
      '-3': 'Motor not initialized. Please restart the device.',
      '-4': 'Invalid timing value. Please check the time range.',
      '-5': 'Failed to save settings. Please try again.',
      '-6': 'Motor hardware error. Please check the device.'
    };
    
    return errorMessages[errorCode?.toString()] || `Motor operation failed (Error code: ${errorCode})`;
  };

  // Wrapper for async command execution with notification
  const executeCommand = async (commandName, commandFn, successMessage) => {
    console.log(`🚀 executeCommand called for: ${commandName}`);
    try {
      const result = await commandFn();
      console.log(`✅ executeCommand result for ${commandName}:`, result);
      
      // Check if result indicates success (>= 0) or error (< 0)
      if (typeof result === 'number') {
        if (result >= 0) {
          showNotification(true, successMessage || `${commandName} command successful`);
        } else {
          // For motor control commands, use specific error messages
          const errorMessage = commandName === 'Motor Control' ? 
            getMotorErrorMessage(result) : 
            `${commandName} command failed`;
          showNotification(false, errorMessage, result);
        }
      } else if (result && result.success) {
        showNotification(true, successMessage || `${commandName} command successful`);
      } else if (result && result.error) {
        showNotification(false, result.error, result.errorCode);
      } else {
        // Default to success if no specific result structure
        showNotification(true, successMessage || `${commandName} command sent`);
      }
    } catch (error) {
      console.error(`❌ executeCommand error for ${commandName}:`, error);
      // For motor control errors, check if we have a specific error code
      const errorMessage = commandName === 'Motor Control' && error.errorCode ? 
        getMotorErrorMessage(error.errorCode) : 
        `${commandName} failed: ${error.message}`;
      showNotification(false, errorMessage, error.errorCode);
    }
  };

  const handleClearErrorsClick = () => {
    executeCommand(
      'Clear Errors',
      () => onClearErrors?.(serial, 'clearErrors'),
      'Errors cleared successfully'
    );
  };

  const handlePrimeClick = () => {
    executeCommand(
      'Prime',
      () => onPrime?.(serial, 'prime'),
      'Prime command executed successfully'
    );
  };

  const handleResetClick = () => {
    executeCommand(
      'Reset',
      () => onReset?.(serial, 'reset'),
      'Device reset initiated'
    );
  };

  const handlePaymentClick = () => {
    // Generate transaction ID with "free" prefix
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const generatedPaymentId = `free_${timestamp}_${random}`;
    
    // Validate and convert inputs
    const validPaymentId = paymentId?.trim() || generatedPaymentId;
    const validDispenseCount = Math.max(1, parseInt(dispenseCount) || 1);
    const validPricePaid = 0; // Always 0 for test payments
    const validSunscreenType = sunscreenType.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '') || 'Coral_Care_SPF30';
    
    // Format: payment_id|dispenses_granted|price_paid|sunscreen_name
    const paymentData = `${validPaymentId}|${validDispenseCount}|${validPricePaid.toFixed(2)}|${validSunscreenType}`;
    
    console.log('Payment confirmation data:', {
      paymentId: validPaymentId,
      dispenseCount: validDispenseCount, 
      pricePaid: validPricePaid,
      sunscreenType: validSunscreenType,
      formatted: paymentData
    });
    
    executeCommand(
      'Payment Confirmation',
      () => onPaymentConfirmed?.(serial, 'paymentConfirmed', { paymentData }),
      `Test payment confirmed: ${validDispenseCount} dispenses of ${validSunscreenType}`
    );
  };

  const handleMotorClick = () => {
    // Validate based on command type
    let validationError = null;
    
    switch (motorCommand) {
      case 'DISPENSE':
        if (!motorValue || motorValue < 0.1 || motorValue > 3) {
          validationError = 'Dispense volume must be between 0.1 and 3 mL';
        }
        break;
        
      case 'SET_FORWARD_TIME':
      case 'SET_REVERSE_TIME':
      case 'RUN':
        if (!motorValue || motorValue < 100 || motorValue > 30000) {
          validationError = 'Time value must be between 100 and 30000 milliseconds';
        }
        break;
        
      case 'SET_FLOW_RATE':
        if (!motorValue || motorValue < 0.1 || motorValue > 100) {
          validationError = 'Flow rate must be between 0.1 and 100 mL/s';
        }
        break;
        
      case 'PRIME':
      case 'START':
      case 'STOP':
      case 'GET_FLOW_RATE':
      case 'GET_TIME':
      case 'GET_REVERSE_TIME':
        // These commands don't need values
        break;
        
      default:
        validationError = 'Invalid motor command';
    }
    
    if (validationError) {
      alert(validationError);
      return;
    }
    
    // Call motorControl backend function with proper command structure
    executeCommand(
      'Motor Control',
      async () => {
        let commandToSend = motorCommand;
        let valueToSend = null;
        
        // Commands that require values
        if (['DISPENSE', 'SET_FORWARD_TIME', 'SET_REVERSE_TIME', 'RUN', 'SET_FLOW_RATE'].includes(motorCommand)) {
          valueToSend = motorValue;
          commandToSend = `${motorCommand}|${motorValue}`;
        }
        
        return await onMotorControl?.(serial, 'motorControl', { 
          motorCommand: commandToSend,
          value: valueToSend
        });
      },
      getMotorCommandSuccessMessage(motorCommand, motorValue)
    );
  };
  
  const getMotorCommandSuccessMessage = (command, value) => {
    switch (command) {
      case 'DISPENSE':
        return `Dispensing ${value} mL of sunscreen`;
      case 'SET_FORWARD_TIME':
        return `Forward dispense time set to ${value}ms`;
      case 'SET_REVERSE_TIME':
        return `Reverse time set to ${value}ms`;
      case 'SET_FLOW_RATE':
        return `Flow rate set to ${value} mL/s`;
      case 'RUN':
        return `Motor running for ${value}ms`;
      case 'PRIME':
        return 'Pump primed successfully (5 seconds)';
      case 'START':
        return 'Motor started';
      case 'STOP':
        return 'Motor stopped';
      case 'GET_FLOW_RATE':
      case 'GET_TIME':
      case 'GET_REVERSE_TIME':
        return `${command} query sent`;
      default:
        return `Motor ${command} command sent`;
    }
  };

  const handleDiagnosticsClick = () => {
    executeCommand(
      'Run Diagnostics',
      () => onRunDiagnostics?.(serial, 'runDiagnostics'),
      'Diagnostics started'
    );
  };

  const handleSensorClick = (sensorType) => {
    // Use 'runDiagnostics' action for sensor operations
    executeCommand(
      `${sensorType} Sensor`,
      () => onSensorControl?.(serial, 'runDiagnostics', { sensorType }),
      `${sensorType} sensor operation completed`
    );
  };

  const handleModeChange = () => {
    executeCommand(
      'Set Mode',
      () => onSetMode?.(serial, 'setMode', { mode: deviceMode }),
      `Device mode changed to ${deviceMode}`
    );
  };

  const handleClearDataClick = () => {
    if (onConfirmDangerousAction) {
      onConfirmDangerousAction(
        'Clear Device Data',
        `Are you sure you want to clear all data from device ${serial}? This action cannot be undone.`,
        'delete',
        `Device ${serial} Data`,
        () => executeCommand(
          'Clear Data',
          () => onClearData?.(serial, 'clearData'),
          'Device data cleared successfully'
        )
      );
    } else {
      executeCommand(
        'Clear Data',
        () => onClearData?.(serial, 'clearData'),
        'Device data cleared successfully'
      );
    }
  };

  const handleFactoryResetClick = () => {
    if (onConfirmDangerousAction) {
      onConfirmDangerousAction(
        'Factory Reset Device',
        `Are you sure you want to factory reset device ${serial}? This will restore all settings to default and cannot be undone.`,
        'reset',
        `Device ${serial}`,
        () => executeCommand(
          'Factory Reset',
          () => onFactoryReset?.(serial, 'factoryReset'),
          'Factory reset completed successfully'
        )
      );
    } else {
      executeCommand(
        'Factory Reset',
        () => onFactoryReset?.(serial, 'factoryReset'),
        'Factory reset completed successfully'
      );
    }
  };

  const renderFavoritesTools = () => (
    <DeviceToolGuard toolType="favorites">
      <div className="tool-section">
        <div className="tool-grid">
  

        {/* Set Mode */}
        <div className="tool-card">
          <div className="tool-header">
            <FiSettings className="tool-icon" />
            <h4>Operating Mode</h4>
          </div>
          <p className="tool-description">Change how customers can use your dispenser</p>
          <div className="tool-input-group">
            <label>Mode:</label>
            <select
              value={deviceMode}
              onChange={(e) => setDeviceMode(e.target.value)}
              className="tool-select"
            >
              {getModeOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button 
            className="tool-btn primary"
            onClick={handleModeChange}
            disabled={isLoading}
          >
            Update Mode
          </button>
        </div>

        {/* Sensor Calibration */}
        <div className="tool-card">
          <div className="tool-header">
            <FiRadio className="tool-icon" />
            <h4>Touch Sensor Setup</h4>
          </div>
          <p className="tool-description">Adjust how sensitive the touch sensor is</p>
          <button 
            className="tool-btn primary"
            onClick={() => onProximity?.(serial, 'calibrateProximity', {})}
            disabled={isLoading}
            title="Calibrate proximity sensor baseline"
          >
            📏 Baseline Sensor
          </button>
        </div>
      </div>
    </div>
    </DeviceToolGuard>
  );

  const renderHardwareTools = () => (
    <DeviceToolGuard toolType="hardware">
      <div className="tool-section">
      <div className="tool-grid">
        {/* Proximity Sensor */}
        <div className="tool-card">
          <div className="tool-header">
            <FiRadio className="tool-icon" />
            <h4>Touch Sensor</h4>
          </div>
          <p className="tool-description">Set up and adjust your touch sensor settings</p>
          <div className="tool-button-group">
            <button 
              className="tool-btn primary"
              onClick={() => onProximity?.(serial, 'calibrateProximity', {})}
              disabled={isLoading}
              title="Calibrate proximity sensor baseline"
            >
              📏 Baseline Sensor
            </button>
            <button 
              className="tool-btn secondary disabled"
              disabled={true}
              title="Configure indoor proximity settings"
            >
              🏠 Indoor Setup
            </button>
            <span className="coming-soon">Coming Soon</span>
            <button 
              className="tool-btn secondary disabled"
              disabled={true}
              title="Configure outdoor proximity settings"
            >
              🌞 Outdoor Setup
            </button>
            <span className="coming-soon">Coming Soon</span>
          </div>
        </div>

        {/* Combined Motor & Pump Control */}
        <div className="tool-card tool-card-wide">
          <div className="tool-header">
            <FiActivity className="tool-icon" />
            <h4>Pump Operations</h4>
          </div>
          <p className="tool-description">Control your sunscreen pump and adjust timing settings</p>
          
          <div className="motor-control-grid">
            <div className="motor-controls-spread">
              <div className="tool-input-group">
                <select
                  value={motorCommand}
                  onChange={(e) => {
                    const cmd = e.target.value;
                    setMotorCommand(cmd);
                    // Set appropriate default values based on command
                    switch (cmd) {
                      case 'DISPENSE':
                        setMotorValue(1.5);
                        break;
                      case 'SET_FLOW_RATE':
                        setMotorValue(10);
                        break;
                      case 'RUN':
                      case 'SET_FORWARD_TIME':
                      case 'SET_REVERSE_TIME':
                        setMotorValue(1000);
                        break;
                      default:
                        setMotorValue(0);
                    }
                  }}
                  className="tool-select"
                >
                  <option value="DISPENSE">Dispense Size (mL)</option>
                  <option value="PRIME">Prime (5s)</option>
                  <option value="RUN">Run Motor</option>
                  <option value="SET_FORWARD_TIME">Forward Time</option>
                  <option value="SET_REVERSE_TIME">Reverse Time</option>
                  <option value="SET_FLOW_RATE">Flow Rate</option>
                </select>
              </div>
              
              {/* Dynamic input based on command */}
              {['DISPENSE', 'SET_FORWARD_TIME', 'SET_REVERSE_TIME', 'RUN', 'SET_FLOW_RATE'].includes(motorCommand) && (
                <div className="tool-input-group input-with-unit" style={{ position: 'relative', display: 'inline-block' }}>
                  <input 
                    type="number"
                    min={motorCommand === 'DISPENSE' ? 0.1 : motorCommand === 'SET_FLOW_RATE' ? 0.1 : 100}
                    max={motorCommand === 'DISPENSE' ? 3 : motorCommand === 'SET_FLOW_RATE' ? 100 : 30000}
                    step={motorCommand === 'DISPENSE' || motorCommand === 'SET_FLOW_RATE' ? 0.1 : 50}
                    value={motorValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        setMotorValue('');
                      } else {
                        const numVal = parseFloat(val);
                        if (!isNaN(numVal)) {
                          setMotorValue(numVal);
                        }
                      }
                    }}
                    className="tool-input tool-input-with-unit"
                    style={{ paddingRight: '40px' }}
                    placeholder={motorCommand === 'DISPENSE' ? '1.5' :
                                motorCommand === 'SET_FLOW_RATE' ? '10' :
                                '1000'}
                  />
                  <span style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    {motorCommand === 'DISPENSE' ? 'mL' :
                     motorCommand === 'SET_FLOW_RATE' ? 'mL/s' :
                     'ms'}
                  </span>
                </div>
              )}
              
              <button 
                className="tool-btn primary motor-execute-btn"
                onClick={handleMotorClick}
                disabled={isLoading || !serial || 
                  (['DISPENSE', 'SET_FORWARD_TIME', 'SET_REVERSE_TIME', 'RUN', 'SET_FLOW_RATE'].includes(motorCommand) && !motorValue)
                }
              >
                {motorCommand === 'PRIME' ? 'Prime' : 
                 motorCommand === 'DISPENSE' ? 'Dispense' :
                 motorCommand === 'RUN' ? 'Run' :
                 motorCommand === 'SET_FORWARD_TIME' ? 'Set Forward' :
                 motorCommand === 'SET_REVERSE_TIME' ? 'Set Reverse' :
                 motorCommand === 'SET_FLOW_RATE' ? 'Set Flow' :
                 'Execute'}
              </button>
            </div>
          </div>
        </div>

        {/* Test Payment */}
        <div className="tool-card tool-card-wide">
          <div className="tool-header">
            <FiDollarSign className="tool-icon" />
            <h4>Test Payment</h4>
          </div>
          <p className="tool-description">Simulate a payment confirmation for testing (free dispenses)</p>
          
          <div className="tool-input-group">
            <label>Payment ID (optional):</label>
            <input
              type="text"
              placeholder="Auto-generated with 'free' prefix"
              value={paymentId}
              onChange={(e) => setPaymentId(e.target.value)}
              className="tool-input"
            />
            <small className="input-hint">Leave empty to auto-generate (format: free_timestamp_random)</small>
          </div>
        
          <div className="tool-input-group">
            <label>Sunscreen Type:</label>
            <input
              type="text"
              placeholder="Coral_Care_SPF30"
              value={sunscreenType}
              onChange={(e) => setSunscreenType(e.target.value)}
              className="tool-input"
            />
            <small className="input-hint">Sunscreen name (alphanumeric, underscore, dash only)</small>
          </div>
        
          <div className="tool-input-group">
            <label>Dispense Count:</label>
            <input
              type="number"
              min="1"
              max="10"
              value={dispenseCount}
              onChange={(e) => setDispenseCount(parseInt(e.target.value))}
              className="tool-input"
            />
            <small className="input-hint">How many times the customer can use the dispenser (1-10)</small>
          </div>
        
          <div className="payment-validation-info">
            <h5>Test Payment Details:</h5>
            <ul className="validation-list">
              <li>Price: Always $0.00 (free test)</li>
              <li>Payment ID: Auto-generated with 'free' prefix if not provided</li>
              <li>Sunscreen Type: Alphanumeric, underscore, and dash only</li>
              <li>Dispense Count: 1-10 dispenses</li>
            </ul>
            <div className="error-codes">
              <strong>Return Codes:</strong>
              <span>0=Success, -1=Invalid format, -2=Invalid dispense count, -3=Invalid price, -4=Invalid payment ID length, -5=Invalid payment ID format, -6=Invalid sunscreen name</span>
            </div>
          </div>
        
          <button 
            className="tool-btn primary"
            onClick={handlePaymentClick}
            disabled={isLoading || dispenseCount < 1}
          >
            Confirm Test Payment
          </button>
        </div>


      </div>
    </div>
    </DeviceToolGuard>
  );

  const renderAdminTools = () => (
    <DeviceToolGuard toolType="advanced">
      <div className="tool-section">
        <div className="tool-grid">




        {/* Reset Device */}
        <div className="tool-card">
          <div className="tool-header">
            <FiZap className="tool-icon" />
            <h4>Reset Device</h4>
          </div>
          <p className="tool-description">Restart device completely</p>
          <button 
            className="tool-btn danger"
            onClick={handleResetClick}
            disabled={isLoading}
          >
            Reset Device
          </button>
        </div>

        {/* Clear Device Data */}
        <div className="tool-card">
          <div className="tool-header">
            <FiTrash2 className="tool-icon" />
            <h4>Clear Device Data</h4>
          </div>
          <p className="tool-description">Clear all stored device data and logs</p>
          <button 
            className="tool-btn danger"
            onClick={handleClearDataClick}
            disabled={isLoading}
          >
            Clear Data
          </button>
        </div>




      </div>
    </div>
    </DeviceToolGuard>
  );

  const renderActiveTools = () => {
    switch (activeToolCategory) {
      case 'sensor':
      case 'hardware':
        return renderHardwareTools();
      case 'admin':
        return renderAdminTools();
      case 'general':
      case 'favorites':
      default:
        return renderFavoritesTools();
    }
  };

  return (
    <div className="device-management-tools">
      <div className="tools-header">
        <h3>Device Management Tools</h3>
        <div className="tool-categories">
          {toolCategories.map(category => (
            <button
              key={category.id}
              className={`category-btn ${activeToolCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveToolCategory(category.id)}
            >
              <category.icon className="category-icon" />
              {category.label}
            </button>
          ))}
        </div>
      </div>

      <div className="tools-content">
        {renderActiveTools()}
      </div>
      
      <CommandNotification
        show={notification.show}
        success={notification.success}
        message={notification.message}
        errorCode={notification.errorCode}
        onClose={() => setNotification({ ...notification, show: false })}
      />
    </div>
  );
};

export default DeviceManagementTools;
